/**
 * CRUD service functions for all entities
 */
import api from './api';
import { API_ROUTES } from '../config/api';
import {
  Article,
  Sale,
  Stock,
  Supplier,
  ArticleSupplier,
  Lifecycle,
  CrudError,
  CreateRequest,
  UpdateRequest,
} from '../types/crud.types';

// Helper function to extract error message from API response
function extractErrorMessage(error: any): CrudError {
  if (error?.response?.data?.error) {
    return { error: error.response.data.error };
  }
  if (error?.response?.data?.detail) {
    return { error: error.response.data.detail };
  }
  if (error?.message) {
    return { error: error.message };
  }
  return { error: 'Une erreur est survenue' };
}

// Articles CRUD
export const articlesCrud = {
  create: async (databaseId: number | string, data: CreateRequest): Promise<Article> => {
    try {
      const response = await api.post(
        `${API_ROUTES.DB_MANAGER.BASE}${API_ROUTES.DB_MANAGER.ARTICLE_CRUD.LIST(databaseId)}`, 
        data
      );
      return response.data;
    } catch (error: any) {
      throw extractErrorMessage(error);
    }
  },

  get: async (databaseId: number | string, articleId: string): Promise<Article> => {
    try {
      const response = await api.get(
        `${API_ROUTES.DB_MANAGER.BASE}${API_ROUTES.DB_MANAGER.ARTICLE_CRUD.DETAIL(databaseId, articleId)}`
      );
      return response.data;
    } catch (error: any) {
      throw extractErrorMessage(error);
    }
  },

  update: async (databaseId: number | string, articleId: string, data: UpdateRequest): Promise<Article> => {
    try {
      const response = await api.put(
        `${API_ROUTES.DB_MANAGER.BASE}${API_ROUTES.DB_MANAGER.ARTICLE_CRUD.DETAIL(databaseId, articleId)}`, 
        data
      );
      return response.data;
    } catch (error: any) {
      throw extractErrorMessage(error);
    }
  },

  delete: async (databaseId: number | string, articleId: string): Promise<void> => {
    try {
      await api.delete(
        `${API_ROUTES.DB_MANAGER.BASE}${API_ROUTES.DB_MANAGER.ARTICLE_CRUD.DETAIL(databaseId, articleId)}`
      );
    } catch (error: any) {
      throw extractErrorMessage(error);
    }
  },
};

// Sales CRUD
export const salesCrud = {
  create: async (databaseId: number | string, data: CreateRequest): Promise<Sale> => {
    try {
      const response = await api.post(
        `${API_ROUTES.DB_MANAGER.BASE}${API_ROUTES.DB_MANAGER.SALES_CRUD.LIST(databaseId)}`, 
        data
      );
      return response.data;
    } catch (error: any) {
      throw extractErrorMessage(error);
    }
  },

  get: async (databaseId: number | string, saleId: number | string): Promise<Sale> => {
    try {
      const response = await api.get(
        `${API_ROUTES.DB_MANAGER.BASE}${API_ROUTES.DB_MANAGER.SALES_CRUD.DETAIL(databaseId, saleId)}`
      );
      return response.data;
    } catch (error: any) {
      throw extractErrorMessage(error);
    }
  },

  update: async (databaseId: number | string, saleId: number | string, data: UpdateRequest): Promise<Sale> => {
    try {
      const response = await api.put(
        `${API_ROUTES.DB_MANAGER.BASE}${API_ROUTES.DB_MANAGER.SALES_CRUD.DETAIL(databaseId, saleId)}`, 
        data
      );
      return response.data;
    } catch (error: any) {
      throw extractErrorMessage(error);
    }
  },

  delete: async (databaseId: number | string, saleId: number | string): Promise<void> => {
    try {
      await api.delete(
        `${API_ROUTES.DB_MANAGER.BASE}${API_ROUTES.DB_MANAGER.SALES_CRUD.DETAIL(databaseId, saleId)}`
      );
    } catch (error: any) {
      throw extractErrorMessage(error);
    }
  },
};

// Stock CRUD
export const stockCrud = {
  create: async (databaseId: number | string, data: CreateRequest): Promise<Stock> => {
    try {
      const response = await api.post(API_ROUTES.STOCK.CRUD.LIST(databaseId), data);
      return response.data;
    } catch (error: any) {
      throw extractErrorMessage(error);
    }
  },

  get: async (databaseId: number | string, stockId: number | string): Promise<Stock> => {
    try {
      const response = await api.get(API_ROUTES.STOCK.CRUD.DETAIL(databaseId, stockId));
      return response.data;
    } catch (error: any) {
      throw extractErrorMessage(error);
    }
  },

  update: async (databaseId: number | string, stockId: number | string, data: UpdateRequest): Promise<Stock> => {
    try {
      const response = await api.put(API_ROUTES.STOCK.CRUD.DETAIL(databaseId, stockId), data);
      return response.data;
    } catch (error: any) {
      throw extractErrorMessage(error);
    }
  },

  delete: async (databaseId: number | string, stockId: number | string): Promise<void> => {
    try {
      await api.delete(API_ROUTES.STOCK.CRUD.DETAIL(databaseId, stockId));
    } catch (error: any) {
      throw extractErrorMessage(error);
    }
  },
};

// Suppliers CRUD
export const suppliersCrud = {
  create: async (databaseId: number | string, data: CreateRequest): Promise<Supplier> => {
    try {
      const response = await api.post(API_ROUTES.SUPPLIERS.CRUD.LIST(databaseId), data);
      return response.data;
    } catch (error: any) {
      throw extractErrorMessage(error);
    }
  },

  get: async (databaseId: number | string, supplierId: number | string): Promise<Supplier> => {
    try {
      const response = await api.get(API_ROUTES.SUPPLIERS.CRUD.DETAIL(databaseId, supplierId));
      return response.data;
    } catch (error: any) {
      throw extractErrorMessage(error);
    }
  },

  update: async (databaseId: number | string, supplierId: number | string, data: UpdateRequest): Promise<Supplier> => {
    try {
      const response = await api.put(API_ROUTES.SUPPLIERS.CRUD.DETAIL(databaseId, supplierId), data);
      return response.data;
    } catch (error: any) {
      throw extractErrorMessage(error);
    }
  },

  delete: async (databaseId: number | string, supplierId: number | string): Promise<void> => {
    try {
      await api.delete(API_ROUTES.SUPPLIERS.CRUD.DETAIL(databaseId, supplierId));
    } catch (error: any) {
      throw extractErrorMessage(error);
    }
  },
};

// Article-Suppliers CRUD
export const articleSuppliersCrud = {
  create: async (databaseId: number | string, data: CreateRequest): Promise<ArticleSupplier> => {
    try {
      const response = await api.post(API_ROUTES.ARTICLE_SUPPLIERS.CRUD.LIST(databaseId), data);
      return response.data;
    } catch (error: any) {
      throw extractErrorMessage(error);
    }
  },

  get: async (databaseId: number | string, articleSupplierId: number | string): Promise<ArticleSupplier> => {
    try {
      const response = await api.get(API_ROUTES.ARTICLE_SUPPLIERS.CRUD.DETAIL(databaseId, articleSupplierId));
      return response.data;
    } catch (error: any) {
      throw extractErrorMessage(error);
    }
  },

  update: async (databaseId: number | string, articleSupplierId: number | string, data: UpdateRequest): Promise<ArticleSupplier> => {
    try {
      const response = await api.put(API_ROUTES.ARTICLE_SUPPLIERS.CRUD.DETAIL(databaseId, articleSupplierId), data);
      return response.data;
    } catch (error: any) {
      throw extractErrorMessage(error);
    }
  },

  delete: async (databaseId: number | string, articleSupplierId: number | string): Promise<void> => {
    try {
      await api.delete(API_ROUTES.ARTICLE_SUPPLIERS.CRUD.DETAIL(databaseId, articleSupplierId));
    } catch (error: any) {
      throw extractErrorMessage(error);
    }
  },
};

// Lifecycles CRUD
export const lifecyclesCrud = {
  create: async (databaseId: number | string, data: CreateRequest): Promise<Lifecycle> => {
    try {
      const response = await api.post(API_ROUTES.LIFE_CYCLE.CRUD.LIST(databaseId), data);
      return response.data;
    } catch (error: any) {
      throw extractErrorMessage(error);
    }
  },

  get: async (databaseId: number | string, lifecycleId: number | string): Promise<Lifecycle> => {
    try {
      const response = await api.get(API_ROUTES.LIFE_CYCLE.CRUD.DETAIL(databaseId, lifecycleId));
      return response.data;
    } catch (error: any) {
      throw extractErrorMessage(error);
    }
  },

  update: async (databaseId: number | string, lifecycleId: number | string, data: UpdateRequest): Promise<Lifecycle> => {
    try {
      const response = await api.put(API_ROUTES.LIFE_CYCLE.CRUD.DETAIL(databaseId, lifecycleId), data);
      return response.data;
    } catch (error: any) {
      throw extractErrorMessage(error);
    }
  },

  delete: async (databaseId: number | string, lifecycleId: number | string): Promise<void> => {
    try {
      await api.delete(API_ROUTES.LIFE_CYCLE.CRUD.DETAIL(databaseId, lifecycleId));
    } catch (error: any) {
      throw extractErrorMessage(error);
    }
  },
};

// Export all CRUD services
export const crudServices = {
  articles: articlesCrud,
  sales: salesCrud,
  stock: stockCrud,
  suppliers: suppliersCrud,
  articleSuppliers: articleSuppliersCrud,
  lifecycles: lifecyclesCrud,
};

